import * as THREE from 'three';
import store from '../../redux/store';
import { detachHotspotToSceneRoot } from '../../utils/hotspotHierarchyUtils';
import { HotSpotInitialData } from '../../utils/constant';
import { sceneStateHelper } from '../../utils/ThreeJsUtils/SceneStateHelper';
import {
  setPushIntersectedObject,
  addSceneHotspot,
  add_new_assets,
  update_assets_data,
} from '../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import toast from 'react-hot-toast';

import { applyStoredPivotToObject } from '../../utils/ThreeJsUtils/pivotEditor';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Panel/scene text as-is; empty string stays empty (no placeholder). */
function hotspotText(value) {
  if (value == null) return '';
  return String(value);
}

/**
 * Measures how wide/tall the wrapped text will be on a canvas.
 * Returns { lines, lineHeight, textW, textH }
 */
function measureText(text, fontSize, fontFamily, maxWidth, padding) {
  const raw = hotspotText(text);
  if (!raw.trim()) {
    const lineHeight = fontSize * 1.3;
    return { lines: [], lineHeight, textW: 0, textH: 0 };
  }

  const offscreen = document.createElement('canvas');
  const ctx = offscreen.getContext('2d');
  ctx.font = `600 ${fontSize}px ${fontFamily}`;

  const words = raw.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);

  const lineHeight = fontSize * 1.3;
  const textW = Math.max(...lines.map((l) => ctx.measureText(l).width));
  const textH = lines.length * lineHeight;
  return { lines, lineHeight, textW, textH };
}

/**
 * Creates a THREE.CanvasTexture sized to fit the text exactly.
 * Returns { texture, canvas, canvasWidth, canvasHeight }
 */
function createTextTexture(text, opts = {}) {
  const {
    bgColor = 'transparent',
    textColor = '#ffffff',
    fontSize = 36,
    fontFamily = 'Arial',
    padding = 20,
    borderColor = '#ffffff',
    borderWidth = 8,
    borderRadius = 32,
    shape = 'Circle', // 'Circle' | 'Rectangle'
    minSize = 64, // minimum canvas dimension
    fixedWidth, // when set with fixedHeight, canvas uses fixed size (button stays same size)
    fixedHeight,
    maxWidth, // max width (px) for text wrapping; when set, text wraps instead of going horizontally
    textureScale = 1, // >1 renders supersampled pixels; logical size unchanged
  } = opts;

  const pixelScale = Math.max(1, Number(textureScale) || 1);

  let canvasWidth, canvasHeight;
  let lines, lineHeight;

  if (fixedWidth != null && fixedHeight != null) {
    // Fixed-size mode: canvas dimensions are fixed; text is centered and may wrap/clip
    canvasWidth =
      shape === 'Circle' ? Math.min(fixedWidth, fixedHeight) : fixedWidth;
    canvasHeight =
      shape === 'Circle' ? Math.min(fixedWidth, fixedHeight) : fixedHeight;
    const maxTextWidth = Math.max(
      1,
      canvasWidth - padding * 2 - borderWidth * 2
    );
    const measured = measureText(
      text,
      fontSize,
      fontFamily,
      maxTextWidth,
      padding
    );
    lines = measured.lines;
    lineHeight = measured.lineHeight;
  } else {
    // Auto-size mode: canvas grows with text; wrap at maxWidth when provided
    const wrapWidth = maxWidth != null ? maxWidth : 1000;
    const measured = measureText(
      text,
      fontSize,
      fontFamily,
      wrapWidth,
      padding
    );
    lines = measured.lines;
    lineHeight = measured.lineHeight;
    const textW = measured.textW;
    const textH = measured.textH;

    if (shape === 'Circle') {
      const diameter = Math.max(
        textW + padding * 2,
        textH + padding * 2,
        minSize
      );
      canvasWidth = canvasHeight = Math.ceil(diameter / 2) * 2;
    } else {
      canvasWidth = Math.max(Math.ceil(textW + padding * 2), minSize);
      canvasHeight = Math.max(Math.ceil(textH + padding * 2), minSize);
      canvasWidth += borderWidth * 2;
      canvasHeight += borderWidth * 2;
    }
  }

  // ── 2. Draw ───────────────────────────────────────────────────────────
  const canvas = document.createElement('canvas');
  const logicalWidth = canvasWidth;
  const logicalHeight = canvasHeight;
  canvas.width = Math.ceil(logicalWidth * pixelScale);
  canvas.height = Math.ceil(logicalHeight * pixelScale);
  const ctx = canvas.getContext('2d');
  ctx.setTransform(pixelScale, 0, 0, pixelScale, 0, 0);
  ctx.imageSmoothingEnabled = true;
  if (typeof ctx.imageSmoothingQuality === 'string') {
    ctx.imageSmoothingQuality = 'high';
  }

  // Shape path helper
  const drawShapePath = () => {
    ctx.beginPath();
    if (shape === 'Circle') {
      ctx.arc(
        logicalWidth / 2,
        logicalHeight / 2,
        Math.min(logicalWidth, logicalHeight) / 2 - borderWidth / 2,
        0,
        Math.PI * 2
      );
    } else {
      const r = borderRadius;
      const bh = borderWidth / 2;
      const x = bh,
        y = bh;
      const w = logicalWidth - borderWidth;
      const h = logicalHeight - borderWidth;
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    }
  };

  // Fill
  if (bgColor && bgColor !== 'transparent') {
    ctx.fillStyle = bgColor;
    drawShapePath();
    ctx.fill();
  }

  // Border
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = borderWidth;
  drawShapePath();
  ctx.stroke();

  // Text — semibold sans reads sharper than heavy bold at billboard scale
  ctx.fillStyle = textColor;
  ctx.font = `600 ${fontSize}px ${fontFamily}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (lines.length > 0) {
    const startY = logicalHeight / 2 - ((lines.length - 1) * lineHeight) / 2;
    lines.forEach((l, i) =>
      ctx.fillText(l, logicalWidth / 2, startY + i * lineHeight)
    );
  }

  const texture = new THREE.CanvasTexture(canvas);
  return {
    texture,
    canvas,
    canvasWidth: logicalWidth,
    canvasHeight: logicalHeight,
  };
}

/** Max wrap width (px) for hotspot message text at large viewports */
const HOTSPOT_MESSAGE_MAX_WIDTH_PX = 280;
/** Default uniform shrink of the message card relative to natural billboard size */
const HOTSPOT_MESSAGE_BASE_SCALE = 0.78;
/** Supersample message canvases for sharper text when billboard-scaled in 3D */
const HOTSPOT_MESSAGE_TEXTURE_SCALE = 2;
/** Readable sans-serif stack for hotspot message cards */
const HOTSPOT_MESSAGE_FONT_FAMILY =
  'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
/** Never shrink the message card below this (relative) scale while fitting */
const HOTSPOT_MESSAGE_MIN_FIT_SCALE = 0.18;
/** Screen-edge padding (px) when keeping the message inside the canvas */
const HOTSPOT_MESSAGE_CANVAS_MARGIN_PX = 6;

function configureHotspotCanvasTexture(texture) {
  if (!texture) return texture;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 4;
  texture.needsUpdate = true;
  return texture;
}

/**
 * Convenience wrapper for the message popup card (always a Rectangle plane).
 */
function createMessageTexture(message, opts = {}) {
  const {
    bgColor = '#333333',
    textColor = '#ffffff',
    fontSize = 18,
    fontFamily = HOTSPOT_MESSAGE_FONT_FAMILY,
    letterSpacing = 0,
    padding = 12,
    maxWidth = HOTSPOT_MESSAGE_MAX_WIDTH_PX,
    borderRadius = 8,
    textureScale = HOTSPOT_MESSAGE_TEXTURE_SCALE,
  } = opts;

  const result = createTextTexture(message, {
    bgColor,
    textColor,
    fontSize,
    fontFamily,
    padding,
    letterSpacing,
    borderColor: '#ffffff',
    borderWidth: 4,
    borderRadius,
    shape: 'Rectangle',
    minSize: 40,
    maxWidth,
    textureScale,
  });
  configureHotspotCanvasTexture(result.texture);
  return result;
}

// Reference distance for constant screen-size scaling (preview): at this distance scale = 1
const HOTSPOT_REF_DISTANCE = 5;
/** Default on-screen size of the hotspot button in preview (px) */
const HOTSPOT_TARGET_PIXEL_SIZE = 32;
/** Fixed texture/canvas size for the hotspot button (px) so button size does not depend on text length */
const HOTSPOT_BUTTON_FIXED_SIZE = 120;
/** Fixed reference world size for preview scaling so all hotspots appear the same on-screen size (200px). */
const HOTSPOT_REFERENCE_WORLD_SIZE = (2 * HOTSPOT_BUTTON_FIXED_SIZE) / 5 / 100;

/**
 * Live canvas / preview-frame size for hotspot layout (browser or framed preview).
 */
function resolveHotspotViewportSize(renderer = null) {
  const win = store.getState()?.window;
  const size = win?.window_size;
  const container =
    typeof document !== 'undefined'
      ? document.getElementById('gridContainer')
      : null;
  const canvas =
    renderer?.domElement ||
    container?.querySelector?.('canvas') ||
    null;

  // Prefer the visible frame (#gridContainer) so browser resize + device frames both work.
  let viewportWidth =
    container?.clientWidth ||
    canvas?.clientWidth ||
    0;
  let viewportHeight =
    container?.clientHeight ||
    canvas?.clientHeight ||
    0;

  if (!viewportWidth || !viewportHeight) {
    if (win?.window_type === 'preview' && size?.width && size?.height) {
      viewportWidth = Number(size.width) || viewportWidth;
      viewportHeight = Number(size.height) || viewportHeight;
    }
  }

  if (!viewportWidth || !viewportHeight) {
    viewportWidth =
      typeof window !== 'undefined' ? window.innerWidth : 1280;
    viewportHeight =
      typeof window !== 'undefined' ? window.innerHeight : 800;
  }

  return {
    viewportWidth: Math.max(1, viewportWidth),
    viewportHeight: Math.max(1, viewportHeight),
  };
}

/**
 * Preview layout derived from the actual canvas size — browser resize and
 * framed preview sizes both flow through the same path.
 */
export function getHotspotPreviewLayout(renderer = null) {
  const { viewportWidth, viewportHeight } =
    resolveHotspotViewportSize(renderer);

  const shorter = Math.min(viewportWidth, viewportHeight);
  const messageMaxWidthPx = Math.round(
    Math.min(
      HOTSPOT_MESSAGE_MAX_WIDTH_PX,
      Math.max(110, viewportWidth * 0.52)
    )
  );
  // Soft preferred on-screen width before canvas clamping kicks in
  const messageMaxScreenPx = Math.min(
    viewportWidth * 0.7,
    Math.max(96, shorter * 0.5)
  );
  const buttonTargetPx = Math.round(
    Math.min(
      HOTSPOT_TARGET_PIXEL_SIZE,
      Math.max(22, shorter * 0.05)
    )
  );
  const fontScale = Math.min(1, Math.max(0.75, viewportWidth / 900));

  return {
    viewportWidth,
    viewportHeight,
    buttonTargetPx,
    messageMaxWidthPx,
    messageBaseScale: HOTSPOT_MESSAGE_BASE_SCALE,
    messageMaxScreenPx,
    messageFontSize: Math.round(18 * fontScale),
    messagePadding: Math.round(10 + 4 * fontScale),
    messageBorderRadius: 8,
    messageFontFamily: HOTSPOT_MESSAGE_FONT_FAMILY,
  };
}

function getMessageMaxWidthForCurrentPreview() {
  return getHotspotPreviewLayout().messageMaxWidthPx;
}

function ensureHotspotUserDataWritable(hotspotGroup) {
  if (!hotspotGroup) return;
  if (!hotspotGroup.userData) {
    hotspotGroup.userData = {};
    return;
  }
  if (!Object.isExtensible(hotspotGroup.userData)) {
    hotspotGroup.userData = { ...hotspotGroup.userData };
  }
}

function setHotspotUserDataField(hotspotGroup, key, value) {
  if (!hotspotGroup) return;
  try {
    ensureHotspotUserDataWritable(hotspotGroup);
    hotspotGroup.userData[key] = value;
  } catch {
    hotspotGroup.userData = { ...(hotspotGroup.userData || {}), [key]: value };
  }
}

function setHotspotUserDataClone(hotspotGroup, key, source, cloneFn) {
  if (!hotspotGroup || !source) return;
  const existing = hotspotGroup.userData?.[key];
  if (existing && typeof existing.copy === 'function') {
    try {
      ensureHotspotUserDataWritable(hotspotGroup);
      existing.copy(source);
      return;
    } catch {
      // frozen reference — replace below
    }
  }
  setHotspotUserDataField(hotspotGroup, key, cloneFn(source));
}

const ensureHotspotBaseQuaternion = (hotspotGroup) => {
  if (!hotspotGroup?.userData) return;
  if (!hotspotGroup.userData.baseQuaternion) {
    setHotspotUserDataClone(
      hotspotGroup,
      'baseQuaternion',
      hotspotGroup.quaternion,
      (q) => q.clone()
    );
  }
};

/** Persist design-time rotation before preview billboard overwrites quaternion. */
export const syncHotspotDesignQuaternion = (hotspotGroup) => {
  if (!hotspotGroup?.userData?.isHotspot) return;
  setHotspotUserDataClone(
    hotspotGroup,
    'baseQuaternion',
    hotspotGroup.quaternion,
    (q) => q.clone()
  );
};

/** Restore design-time rotation after preview face-camera billboard. */
export const restoreHotspotDesignRotation = (hotspotGroup) => {
  if (!hotspotGroup?.isGroup) return;
  const baseQ = hotspotGroup.userData?.baseQuaternion;
  if (!baseQ) return;
  hotspotGroup.quaternion.copy(baseQ);
  hotspotGroup.rotation.setFromQuaternion(baseQ, hotspotGroup.rotation.order);
  hotspotGroup.updateMatrix();
  hotspotGroup.updateMatrixWorld(true);
};

/** Stop preview face-camera immediately and restore design rotation. */
export const prepareHotspotsForDesignMode = (hotspotList = []) => {
  hotspotList.forEach((hp) => {
    if (!hp?.isGroup) return;
    const buttonMesh = hp.getObjectByName(`${hp.name}-button`);
    if (buttonMesh) {
      buttonMesh.onBeforeRender = () => {};
    }
    restoreHotspotDesignRotation(hp);
    if (hp.userData?.baseScale) {
      hp.scale.copy(hp.userData.baseScale);
      hp.updateMatrixWorld(true);
    }
  });
};

/** Fixed viewport height (px) for the 200px formula — same in design and preview so hotspot is always same world size and never tiny */
const HOTSPOT_VIEWPORT_REF_HEIGHT = 500;
const _hotspotWorldPos = new THREE.Vector3();
const _hotspotParentScale = new THREE.Vector3();
const _hotspotBox = new THREE.Box3();
const _hotspotBoxSize = new THREE.Vector3();

// ─── Popup Animation Manager ─────────────────────────────────────────────────

/**
 * Map of active fade animations: meshUUID → { mesh, targetOpacity, speed }
 */
const popupAnimations = new Map();

/**
 * Runs every animation frame and lerps opacity toward the target.
 * Auto-hides mesh when fade-out completes.
 */
function tickPopupAnimations() {
  for (const [id, anim] of popupAnimations) {
    const { mesh, targetOpacity, speed } = anim;
    const current = mesh.material.opacity;
    const delta = targetOpacity - current;

    if (Math.abs(delta) < 0.01) {
      // Animation complete — snap to target
      mesh.material.opacity = targetOpacity;
      if (targetOpacity === 0) {
        mesh.visible = false;
      }
      popupAnimations.delete(id);
    } else {
      mesh.material.opacity += delta * speed;
    }

    // Mark material as needing update
    mesh.material.needsUpdate = true;
  }
  requestAnimationFrame(tickPopupAnimations);
}

// Start the animation loop once globally
tickPopupAnimations();

/**
 * Shows or hides a message mesh using the configured popup type.
 *
 * @param {THREE.Mesh}         mesh        - The message mesh to animate
 * @param {boolean}            show        - true = show, false = hide
 * @param {'normal'|'fadeIn'}  popupType   - Animation style
 * @param {number}             baseOpacity - Target opacity when fully visible (0–1)
 */
function setMessageVisible(mesh, show, popupType = 'normal', baseOpacity = 1) {
  const id = mesh.uuid;

  if (popupType === 'fadeIn') {
    if (show) {
      // Start fully transparent then fade in
      mesh.material.opacity = 0;
      mesh.visible = true;
    }
    // Queue the fade animation; if hiding let tickPopupAnimations hide it
    popupAnimations.set(id, {
      mesh,
      targetOpacity: show ? baseOpacity : 0,
      speed: 0.08, // Fraction of remaining distance per frame (~12 frames to 99%)
    });
  } else {
    // Normal: instant show / hide
    popupAnimations.delete(id); // Cancel any running animation
    mesh.material.opacity = show ? baseOpacity : 0;
    mesh.visible = show;
  }
}

// ─── Raycaster / Interaction Registry ───────────────────────────────────────

const hotspotRegistry = {
  hotspots: new Map(),
  raycaster: new THREE.Raycaster(),
  mouse: new THREE.Vector2(),
  camera: null,
  renderer: null,
  _bound: false,
  previewMode: false,

  /**
   * @param {string}            id
   * @param {THREE.Group}       group
   * @param {THREE.Mesh}        messageMesh
   * @param {'Hover'|'Click'}   interactionType
   * @param {THREE.Camera}      camera
   * @param {THREE.WebGLRenderer} renderer
   * @param {'normal'|'fadeIn'} popupType
   * @param {number}            messageOpacity
   */
  register(
    id,
    group,
    messageMesh,
    interactionType,
    camera,
    renderer,
    popupType = 'normal',
    messageOpacity = 1
  ) {
    this.hotspots.set(id, {
      group,
      messageMesh,
      interactionType,
      popupType,
      messageOpacity,
    });

    if (!this._bound && renderer) {
      this.camera = camera;
      this.renderer = renderer;
      const canvas = renderer.domElement;
      // Pointer events cover mouse, trackpad, touch, and pen (one path only).
      this._onPointerMove = this._onMouseMove.bind(this);
      this._onPointerUp = this._onClick.bind(this);
      canvas.addEventListener('pointermove', this._onPointerMove);
      canvas.addEventListener('pointerup', this._onPointerUp);
      try {
        canvas.style.touchAction = 'none';
      } catch (_) {
        /* ignore */
      }
      this._bound = true;
    }
  },

  unregister(id) {
    this.hotspots.delete(id);
  },

  _getIntersectedHotspot(event) {
    if (!this.camera || !this.renderer) return null;
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    for (const [, entry] of this.hotspots) {
      if (this.raycaster.intersectObject(entry.group, true).length > 0)
        return entry;
    }
    return null;
  },

  /**
   * Returns the hotspot entry only if the ray hits the *button* mesh specifically.
   * Ignores hits on the message mesh so invisible message area can't be clicked.
   */
  _getIntersectedButton(event) {
    if (!this.camera || !this.renderer) return null;
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    for (const [, entry] of this.hotspots) {
      const hits = this.raycaster.intersectObject(entry.group, true);
      // Only count the hit if the first intersected object is the button mesh
      if (hits.length > 0 && hits[0].object.userData.isbutton) return entry;
    }
    return null;
  },

  _onMouseMove(event) {
    // Hover still uses full group hit (button area is enough for hover UX)
    const hit = this._getIntersectedHotspot(event);
    for (const [, entry] of this.hotspots) {
      if (entry.interactionType === 'Hover') {
        const shouldShow = hit === entry;
        setMessageVisible(
          entry.messageMesh,
          shouldShow,
          entry.popupType,
          entry.messageOpacity
        );
      }
    }
  },

  _onClick(event) {
    if (this.previewMode) return;
    // Ignore non-primary multi-touch / non-primary mouse buttons.
    if (event.isPrimary === false) return;
    if (
      event.pointerType !== 'touch' &&
      event.pointerType !== 'pen' &&
      typeof event.button === 'number' &&
      event.button !== 0
    ) {
      return;
    }

    const hit = this._getIntersectedButton(event);
    if (!hit) return;

    const isTouch = event.pointerType === 'touch';
    // Click hotspots always toggle; Hover hotspots toggle on touch (no hover).
    if (hit.interactionType !== 'Click' && !(isTouch && hit.interactionType === 'Hover')) {
      return;
    }

    const isCurrentlyVisible =
      hit.messageMesh.visible && hit.messageMesh.material.opacity > 0;
    setMessageVisible(
      hit.messageMesh,
      !isCurrentlyVisible,
      hit.popupType,
      hit.messageOpacity
    );
  },
};

// ─── Hotspot Naming Helper ──────────────────────────────────────────────────

/**
 * Gets the next available hotspot name incrementally (hotspot, hotspot1, hotspot2, etc.)
 * @returns {string} The next hotspot name
 */
function getHotspotDisplayName(hotspotItem, hotspotObj) {
  const internalId =
    hotspotItem?._id || hotspotItem?.id || hotspotObj?._id || hotspotObj?.name;

  const isInternalId = (value) =>
    !value ||
    value === internalId ||
    (typeof value === 'string' &&
      (/^hotspot-\d+$/.test(value) || value.includes('hotspot-group')));

  const candidates = [
    hotspotItem?.file_name,
    hotspotObj?.file_name,
    hotspotObj?.userData?.file_name,
    hotspotItem?.name,
  ];

  for (const candidate of candidates) {
    if (candidate && !isInternalId(candidate)) {
      return candidate;
    }
  }

  return hotspotItem?.file_name || hotspotItem?.name || 'Hotspot';
}

function countHotspotCopies(hotspotAssets, baseName) {
  return hotspotAssets.filter((hs) => {
    const name = hs.file_name || hs.name || hs.temp_file_name || '';
    return name.startsWith(`${baseName}_copy`);
  }).length;
}

function syncDuplicatedHotspotInRedux(newHotspotGroup, copyName, attempt = 0) {
  const hotspotAssets =
    store.getState().assets_object?.assets_data?.hotspot || [];
  const assetIndex = hotspotAssets.findIndex(
    (asset) =>
      asset._id === newHotspotGroup._id || asset.id === newHotspotGroup._id
  );

  if (assetIndex === -1) {
    if (attempt < 10) {
      setTimeout(() => syncDuplicatedHotspotInRedux(newHotspotGroup, copyName, attempt + 1), 0);
    }
    return;
  }

  const newAssets = [...hotspotAssets];
  newAssets[assetIndex] = {
    ...newAssets[assetIndex],
    file_name: copyName,
    name: copyName,
    position_x: newHotspotGroup.position.x,
    position_y: newHotspotGroup.position.y,
    position_z: newHotspotGroup.position.z,
    rotation_x: radToDeg(newHotspotGroup.rotation.x),
    rotation_y: radToDeg(newHotspotGroup.rotation.y),
    rotation_z: radToDeg(newHotspotGroup.rotation.z),
    scale_x: newHotspotGroup.scale.x,
    scale_y: newHotspotGroup.scale.y,
    scale_z: newHotspotGroup.scale.z,
  };

  store.dispatch(update_assets_data({ type: 'hotspot', data: newAssets }));
}

function getNextHotspotName() {
  const currentState = store.getState();
  const hotspotAssets = currentState.assets_object?.assets_data?.hotspot || [];

  if (hotspotAssets.length === 0) return 'Hotspot';

  let maxNum = 0;

  hotspotAssets.forEach((asset) => {
    const assetName = asset.name || asset.file_name || '';

    if (assetName === 'Hotspot') {
      // First one without number
    } else if (assetName.startsWith('Hotspot ')) {
      const numPart = assetName.substring(8); // Remove "Hotspot "
      const num = parseInt(numPart, 10);

      if (!isNaN(num) && num > maxNum) {
        maxNum = num;
      }
    }
  });

  return maxNum === 0 ? 'Hotspot 1' : `Hotspot ${maxNum + 1}`;
}

// ─── Geometry Factory Helpers ─────────────────────────────────────────────────

/**
 * Creates the appropriate geometry for the button based on shape.
 *
 * Circle  → THREE.CircleGeometry(radius, segments)
 * Rectangle → THREE.PlaneGeometry(width, height)
 */
function createButtonGeometry(shape, canvasWidth, canvasHeight, pxPerUnit) {
  if (shape === 'Circle') {
    // Use the radius derived from the square canvas (width === height for circles)
    const radius = canvasWidth / 5 / pxPerUnit;
    return new THREE.CircleGeometry(radius, 64);
  } else {
    const width = canvasWidth / pxPerUnit;
    const height = canvasHeight / pxPerUnit;
    return new THREE.PlaneGeometry(width / 3, height / 3);
  }
}

/**
 * Creates the appropriate geometry for the message card.
 * Message cards are always Rectangle, so always PlaneGeometry.
 */
function createMessageGeometry(canvasWidth, canvasHeight, pxPerUnit) {
  const width = canvasWidth / pxPerUnit;
  const height = canvasHeight / pxPerUnit;
  return new THREE.PlaneGeometry(width, height);
}

/** How far into the button corner the message sits (0–1 of button radius). */
const HOTSPOT_MESSAGE_CORNER_INSET = 0.4;

const _msgCornerLocal = new THREE.Vector3();
const _msgCornerWorld = new THREE.Vector3();

/** Local half-extents of the hotspot button mesh. */
function getHotspotButtonHalfExtents(buttonMesh) {
  if (buttonMesh?.geometry) {
    if (!buttonMesh.geometry.boundingSphere) {
      buttonMesh.geometry.computeBoundingSphere();
    }
    const r = buttonMesh.geometry.boundingSphere?.radius;
    if (Number.isFinite(r) && r > 0) {
      return { halfW: r, halfH: r };
    }
    const params = buttonMesh.geometry.parameters;
    if (params?.radius != null) {
      return { halfW: params.radius, halfH: params.radius };
    }
    if (params?.width != null || params?.height != null) {
      return {
        halfW: (params.width || HOTSPOT_REFERENCE_WORLD_SIZE) / 2,
        halfH: (params.height || HOTSPOT_REFERENCE_WORLD_SIZE) / 2,
      };
    }
  }
  const fallback = HOTSPOT_REFERENCE_WORLD_SIZE / 2;
  return { halfW: fallback, halfH: fallback };
}

/**
 * Place the rectangular message card next to the button, tightly attached.
 * side: 'right' | 'left' — which side of the button the card hangs off.
 */
function positionHotspotMessageNearButton(
  messageMesh,
  buttonMesh,
  msgWorldW,
  msgWorldH,
  msgScale = 1,
  side = 'right'
) {
  if (!messageMesh) return;
  const { halfW: btnHalfW, halfH: btnHalfH } =
    getHotspotButtonHalfExtents(buttonMesh);

  const scale =
    Number.isFinite(msgScale) && msgScale > 0
      ? msgScale
      : Math.abs(messageMesh.scale?.x) || 1;
  if (messageMesh.scale.x !== scale) {
    messageMesh.scale.setScalar(scale);
  }

  const geoW = messageMesh.geometry?.parameters?.width;
  const geoH = messageMesh.geometry?.parameters?.height;
  const width = (Number.isFinite(geoW) && geoW > 0 ? geoW : msgWorldW) || 0;
  const height = (Number.isFinite(geoH) && geoH > 0 ? geoH : msgWorldH) || 0;
  const halfW = (width * scale) / 2;
  const halfH = (height * scale) / 2;

  const cornerX = btnHalfW * HOTSPOT_MESSAGE_CORNER_INSET;
  const cornerY = btnHalfH * HOTSPOT_MESSAGE_CORNER_INSET;
  const sign = side === 'left' ? -1 : 1;
  // Bottom corner of the card sits on the button top corner (slight overlap).
  messageMesh.position.set(sign * (cornerX + halfW), cornerY + halfH, 0);
}

/** Axis-aligned screen bounds (px) of a local plane mesh. */
function getMessageScreenAabb(messageMesh, camera, canvasW, canvasH) {
  const geom = messageMesh?.geometry;
  if (!geom || !camera || !canvasW || !canvasH) return null;
  if (!geom.boundingBox) geom.computeBoundingBox();
  const box = geom.boundingBox;
  if (!box) return null;

  messageMesh.updateWorldMatrix(true, false);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const xs = [box.min.x, box.max.x];
  const ys = [box.min.y, box.max.y];
  const zs = [box.min.z, box.max.z];

  for (let ix = 0; ix < 2; ix++) {
    for (let iy = 0; iy < 2; iy++) {
      for (let iz = 0; iz < 2; iz++) {
        _msgCornerLocal.set(xs[ix], ys[iy], zs[iz]);
        messageMesh.localToWorld(_msgCornerWorld.copy(_msgCornerLocal));
        _msgCornerWorld.project(camera);
        if (!Number.isFinite(_msgCornerWorld.x) || !Number.isFinite(_msgCornerWorld.y)) {
          continue;
        }
        const sx = (_msgCornerWorld.x * 0.5 + 0.5) * canvasW;
        const sy = (-_msgCornerWorld.y * 0.5 + 0.5) * canvasH;
        minX = Math.min(minX, sx);
        maxX = Math.max(maxX, sx);
        minY = Math.min(minY, sy);
        maxY = Math.max(maxY, sy);
      }
    }
  }

  if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return null;
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

function measureMessageOverflow(bounds, canvasW, canvasH, margin) {
  if (!bounds) return { total: 0, left: 0, right: 0, top: 0, bottom: 0 };
  const left = Math.max(0, margin - bounds.minX);
  const right = Math.max(0, bounds.maxX - (canvasW - margin));
  const top = Math.max(0, margin - bounds.minY);
  const bottom = Math.max(0, bounds.maxY - (canvasH - margin));
  return {
    left,
    right,
    top,
    bottom,
    total: left + right + top + bottom,
  };
}

/**
 * Shrink (and flip side if needed) so the message stays inside the canvas
 * while remaining attached next to the button.
 */
function fitHotspotMessageInsideCanvas({
  messageMesh,
  buttonMesh,
  hotspotGroup,
  camera,
  renderer,
  msgWorldW,
  msgWorldH,
  preferredScale,
}) {
  if (!messageMesh) return preferredScale;

  const canvas = renderer?.domElement;
  const container =
    typeof document !== 'undefined'
      ? document.getElementById('gridContainer')
      : null;
  const canvasW =
    container?.clientWidth || canvas?.clientWidth || 0;
  const canvasH =
    container?.clientHeight || canvas?.clientHeight || 0;

  let scale = Math.max(
    HOTSPOT_MESSAGE_MIN_FIT_SCALE,
    Number.isFinite(preferredScale) && preferredScale > 0
      ? preferredScale
      : HOTSPOT_MESSAGE_BASE_SCALE
  );

  if (!canvasW || !canvasH || !camera) {
    positionHotspotMessageNearButton(
      messageMesh,
      buttonMesh,
      msgWorldW,
      msgWorldH,
      scale,
      'right'
    );
    return scale;
  }

  const margin = HOTSPOT_MESSAGE_CANVAS_MARGIN_PX;
  const maxW = Math.max(24, canvasW - margin * 2);
  const maxH = Math.max(24, canvasH - margin * 2);

  const trySide = (side, nextScale) => {
    positionHotspotMessageNearButton(
      messageMesh,
      buttonMesh,
      msgWorldW,
      msgWorldH,
      nextScale,
      side
    );
    hotspotGroup?.updateMatrixWorld?.(true);
    const bounds = getMessageScreenAabb(messageMesh, camera, canvasW, canvasH);
    return {
      bounds,
      overflow: measureMessageOverflow(bounds, canvasW, canvasH, margin),
    };
  };

  let side = 'right';
  let { bounds, overflow } = trySide(side, scale);

  // Prefer the side that needs less horizontal shrink for this size.
  if (overflow.total > 0) {
    const leftTry = trySide('left', scale);
    if (leftTry.overflow.total < overflow.total) {
      side = 'left';
      bounds = leftTry.bounds;
      overflow = leftTry.overflow;
    } else {
      // Restore preferred right placement before shrink loop
      ({ bounds, overflow } = trySide('right', scale));
      side = 'right';
    }
  }

  for (let i = 0; i < 10 && overflow.total > 0.5 && scale > HOTSPOT_MESSAGE_MIN_FIT_SCALE; i++) {
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) break;

    const fitBySize = Math.min(maxW / bounds.width, maxH / bounds.height, 1);
    const nextScale = Math.max(
      HOTSPOT_MESSAGE_MIN_FIT_SCALE,
      scale * Math.min(fitBySize, 0.92) * 0.98
    );
    if (Math.abs(nextScale - scale) < 0.002) break;
    scale = nextScale;
    ({ bounds, overflow } = trySide(side, scale));

    // After shrinking, re-check whether the other side now fits better.
    if (overflow.total > 0.5) {
      const other = side === 'right' ? 'left' : 'right';
      const otherTry = trySide(other, scale);
      if (otherTry.overflow.total + 0.5 < overflow.total) {
        side = other;
        bounds = otherTry.bounds;
        overflow = otherTry.overflow;
      } else {
        ({ bounds, overflow } = trySide(side, scale));
      }
    }
  }

  positionHotspotMessageNearButton(
    messageMesh,
    buttonMesh,
    msgWorldW,
    msgWorldH,
    scale,
    side
  );
  return scale;
}

// ─── Main Export ─────────────────────────────────────────────────────────────

/**
 * Adds a hotspot group to the Three.js scene.
 *
 * Both the button and the message card auto-size to their text content.
 * The button always faces the camera (billboard). The message card is
 * positioned above the button in local space so it stays locked to it.
 *
 * Button shape:
 *   - 'Circle'    → THREE.CircleGeometry
 *   - 'Rectangle' → THREE.PlaneGeometry
 *
 * Message card is always PlaneGeometry (rectangular).
 *
 * Popup types:
 *   - 'normal'  → instant show / hide
 *   - 'fadeIn'  → smooth opacity fade in / out
 *
 * Hotspots are always added at scene root (no parent-child with other objects).
 */
const radToDeg = (rad) => (Number(rad) * 180) / Math.PI;

export const handleAddHotSpot = (
  object,
  data,
  existingId = null,
  parentObject = null,
  explicitParentId = null,
  showToast = true,
  nameOverride = null
) => {
  const { scene, camera, renderer } = object;

  if (!scene) {
    console.warn('handleAddHotSpot: scene is undefined.');
    return;
  }

  const tempId = existingId || `hotspot-${Date.now()}`;
  const hotspotGroup = new THREE.Group();
  hotspotGroup.name = existingId || tempId;
  hotspotGroup._id = tempId;
  hotspotGroup.url = tempId;
  hotspotGroup.deleteType = 'hotspot';
  // hotspotGroup.glb_type = 'glb'; // Removed or set to a valid value if needed

  const isRect = data.buttonShape === 'Rectangle';
  const buttonShape = isRect ? 'Rectangle' : 'Circle';

  // ── 1. Button — auto-size to fit text content ──
  const buttonLabel = hotspotText(data.buttonText);

  const {
    texture: buttonTexture,
    canvasWidth: btnCanvasW,
    canvasHeight: btnCanvasH,
  } = createTextTexture(buttonLabel, {
    bgColor: data.buttonColor || '#4A90D9',
    textColor: data.buttonTextColor || '#ffffff',
    fontSize: 36,
    padding: 24,
    borderColor: '#ffffff',
    borderWidth: 8,
    borderRadius: 40,
    shape: buttonShape,
    minSize: 80,
    maxWidth: 300, // Wrap text at 300px width
  });

  // World-space size: scale canvas pixels → Three.js units (100 px = 1 unit)
  const PX_PER_UNIT = 100;

  // ── Circle → CircleGeometry | Rectangle → PlaneGeometry ──────────────
  const buttonGeometry = createButtonGeometry(
    buttonShape,
    btnCanvasW,
    btnCanvasH,
    PX_PER_UNIT
  );

  const buttonMaterial = new THREE.MeshBasicMaterial({
    map: buttonTexture,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: data.buttonOpacity ?? 1,
    alphaTest: 0.01,
    depthTest: false,
    depthWrite: false,
    fog: false,
  });
  const buttonMesh = new THREE.Mesh(buttonGeometry, buttonMaterial);
  buttonMesh.name = `${tempId}-button`;
  buttonMesh.userData.isbutton = true;
  hotspotGroup.add(buttonMesh);

  // ── 2. Message Plane — always PlaneGeometry (rectangular card) ────────
  const messageText = hotspotText(data.messageText);
  const messageOpacity = data.messageOpacity ?? 1;
  const popupType = data.messagePopupType || 'normal'; // 'normal' | 'fadeIn'

  const messageLayout = getHotspotPreviewLayout();
  const {
    texture: messageTexture,
    canvasWidth: msgCanvasW,
    canvasHeight: msgCanvasH,
  } = createMessageTexture(messageText, {
    bgColor: data.messageBgColor || '#222222',
    textColor: data.messageTextColor || '#ffffff',
    fontFamily: messageLayout.messageFontFamily,
    fontSize: messageLayout.messageFontSize,
    padding: messageLayout.messagePadding,
    maxWidth: messageLayout.messageMaxWidthPx,
    borderRadius: messageLayout.messageBorderRadius ?? 8,
  });

  // Message card is always a rectangle → PlaneGeometry
  const messageGeometry = createMessageGeometry(
    msgCanvasW,
    msgCanvasH,
    PX_PER_UNIT
  );
  const msgWorldW = msgCanvasW / PX_PER_UNIT;
  const msgWorldH = msgCanvasH / PX_PER_UNIT;

  const messageMaterial = new THREE.MeshBasicMaterial({
    map: messageTexture,
    color: '#ffffff',
    transparent: true,
    // Always start at 0 opacity so fadeIn has the correct initial state
    opacity: 0,
    side: THREE.DoubleSide,
    alphaTest: 0.01,
    depthTest: false,
    depthWrite: false,
    fog: false,
  });
  const messageMesh = new THREE.Mesh(messageGeometry, messageMaterial);
  messageMesh.name = `${tempId}-message`;
  messageMesh.userData.nonSelectable = true;
  messageMesh.userData.messageWorldWidth = msgWorldW;
  messageMesh.userData.messageWorldHeight = msgWorldH;
  messageMesh.userData.messageLayoutMaxWidthPx =
    messageLayout.messageMaxWidthPx;
  messageMesh.raycast = () => null;

  // Sit tightly beside the button (not offset by full canvas size).
  const initialMsgScale =
    messageLayout.messageBaseScale || HOTSPOT_MESSAGE_BASE_SCALE;
  messageMesh.scale.setScalar(initialMsgScale);
  positionHotspotMessageNearButton(
    messageMesh,
    buttonMesh,
    msgWorldW,
    msgWorldH,
    initialMsgScale
  );
  messageMesh.visible = false;
  hotspotGroup.add(messageMesh);

  // ── 3. userData ───────────────────────────────────────────────────────
  hotspotGroup.userData = {
    hotspot: {
      ...data,
      id: tempId,
      buttonText: hotspotText(data.buttonText),
      messageText: hotspotText(data.messageText),
    },
    isHotspot: true,
    interactionType: data.functionType || 'Click',
    popupType,
    messageOpacity,
  };
  hotspotGroup.userData.isbutton = true;
  hotspotGroup.file_name = tempId;
  hotspotGroup.temp_file_name = tempId + 'hotspot-group';

  _hotspotBox.setFromObject(hotspotGroup);
  _hotspotBox.getSize(_hotspotBoxSize);
  hotspotGroup.userData.baseWorldSize =
    Math.max(_hotspotBoxSize.x, _hotspotBoxSize.y, _hotspotBoxSize.z) || 1;

  // Store original scale for restoring when exiting preview mode
  setHotspotUserDataField(
    hotspotGroup,
    'baseScale',
    new THREE.Vector3(1, 1, 1)
  );
  syncHotspotDesignQuaternion(hotspotGroup);

  scene.add(hotspotGroup);
  detachHotspotToSceneRoot(scene, hotspotGroup);

  // ── 3b. Parent-child: if a non-hotspot object is selected, attach hotspot as its child ─
  // Keep hotspot world size roughly constant by counter-scaling against parent's world scale.
  //   let parentId = explicitParentId ?? null;
  //   if (
  //     parentObject &&
  //     parentObject !== scene &&
  //     !parentObject.userData?.isHotspot
  //   ) {
  //     // Ensure full hierarchy world matrices are up to date before reading parent scale
  //     scene.updateMatrixWorld(true);
  //     const parentWorldScale = new THREE.Vector3();
  //     parentObject.getWorldScale(parentWorldScale);
  //     const sx = Math.abs(parentWorldScale.x) || 1;
  //     const sy = Math.abs(parentWorldScale.y) || 1;
  //     const sz = Math.abs(parentWorldScale.z) || 1;

  //     // Remove from scene root and attach under parent
  //     scene.remove(hotspotGroup);
  //     parentObject.attach(hotspotGroup);

  //     // Counter-scale so hotspot world size stays 1 (not affected by parent scale)
  //     hotspotGroup.scale.set(1 / sx, 1 / sy, 1 / sz);
  //     hotspotGroup.updateMatrixWorld(true);

  //     parentId =
  //       parentObject.name ??
  //       parentObject._id ??
  //       parentObject.id ??
  //       parentObject.temp_file_name ??
  //       null;
  //   }

  // // ── 4. Billboard — always face the camera ─────────────────────────────
  // buttonMesh.onBeforeRender = (_renderer, _scene, cam) => {
  //     cam.getWorldQuaternion(hotspotGroup.quaternion);
  // };

  // ── 5. Register interaction manager ──────────────────────────────────
  if (camera && renderer) {
    hotspotRegistry.register(
      tempId,
      hotspotGroup,
      messageMesh,
      hotspotGroup.userData.interactionType,
      camera,
      renderer,
      popupType, // ← passed to registry
      messageOpacity // ← passed to registry
    );
  } else {
    // Fallback: store refs on userData for external raycaster systems
    hotspotGroup.userData._messageMesh = messageMesh;
  }

  // ── 6. Redux ──────────────────────────────────────────────────────────
  const hotspotName = nameOverride || getNextHotspotName();
  store.dispatch(setPushIntersectedObject(hotspotGroup));
  store.dispatch(addSceneHotspot(hotspotGroup));
  store.dispatch(
    add_new_assets({
      type: 'hotspot',
      data: {
        _id: tempId,
        id: tempId,
        name: hotspotName,
        file_name: hotspotName,
        url: tempId,
        deleteType: 'hotspot',
        objType: '3d',
        userData: hotspotGroup.userData,
        parent: '',
        buttonShape: data.buttonShape || 'Circle',
        buttonText: hotspotText(data.buttonText),
        buttonColor: data.buttonColor || '#4A90D9',
        buttonTextColor: data.buttonTextColor || '#ffffff',
        buttonOpacity: data.buttonOpacity ?? 1,
        autoFocus: data.autoFocus || false,
        functionType: data.functionType || 'Click',
        messageShape: data.messageShape || 'Rectangle',
        messageText: hotspotText(data.messageText),
        messageBgColor: data.messageBgColor || '#222222',
        messageTextColor: data.messageTextColor || '#ffffff',
        messageOpacity: data.messageOpacity ?? 1,
        messagePopupType: data.messagePopupType || 'normal',
        focusDistance: data.focusDistance ?? 10,
        position_x: hotspotGroup.position.x,
        position_y: hotspotGroup.position.y,
        position_z: hotspotGroup.position.z,
        rotation_x: radToDeg(hotspotGroup.rotation.x),
        rotation_y: radToDeg(hotspotGroup.rotation.y),
        rotation_z: radToDeg(hotspotGroup.rotation.z),
        scale_x: hotspotGroup.scale.x,
        scale_y: hotspotGroup.scale.y,
        scale_z: hotspotGroup.scale.z,
      },
    })
  );

  selectObjectInScene(tempId);
  if (showToast) toast.success('Hotspot added to scene successfully!');

  return hotspotGroup;
};

/**
 * Updates an existing hotspot group with new data (button/message appearance and settings).
 * Use when the user has a hotspot selected and changes values in the HotSpot component panel.
 */
export const handleUpdateHotSpot = (hotspotGroup, data, object = {}) => {
  const { camera, renderer, previewMessageOpacity = false } = object;
  if (!hotspotGroup?.userData?.isHotspot) {
    console.warn('handleUpdateHotSpot: not a hotspot group.');
    return;
  }

  const tempId = hotspotGroup.name || hotspotGroup._id || hotspotGroup.url;
  if (!tempId) return;

  // Unregister so we can replace meshes and re-register with new refs
  hotspotRegistry.unregister(tempId);

  // Preserve message visibility state before removing old meshes
  const oldMessageMesh =
    hotspotGroup.userData._messageMesh ||
    hotspotGroup.getObjectByName(`${tempId}-message`);
  const messageWasVisible = oldMessageMesh?.visible ?? false;

  // Remove and dispose old button and message meshes
  while (hotspotGroup.children.length > 0) {
    const child = hotspotGroup.children[0];
    hotspotGroup.remove(child);
    if (child.geometry) child.geometry.dispose();
    if (child.material) {
      if (child.material.map) child.material.map.dispose();
      child.material.dispose();
    }
  }

  const isRect = data.buttonShape === 'Rectangle';
  const buttonShape = isRect ? 'Rectangle' : 'Circle';
  const buttonLabel = hotspotText(data.buttonText);

  const {
    texture: buttonTexture,
    canvasWidth: btnCanvasW,
    canvasHeight: btnCanvasH,
  } = createTextTexture(buttonLabel, {
    bgColor: data.buttonColor || '#4A90D9',
    textColor: data.buttonTextColor || '#ffffff',
    fontSize: 36,
    padding: 24,
    borderColor: '#ffffff',
    borderWidth: 8,
    borderRadius: 40,
    shape: buttonShape,
    minSize: 80,
    maxWidth: 300, // Wrap text at 300px width
  });

  const buttonGeometry = createButtonGeometry(
    buttonShape,
    btnCanvasW,
    btnCanvasH,
    100
  );
  const buttonMaterial = new THREE.MeshBasicMaterial({
    map: buttonTexture,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: data.buttonOpacity ?? 1,
    alphaTest: 0.01,
    depthTest: false,
    depthWrite: false,
    fog: false,
  });
  const buttonMesh = new THREE.Mesh(buttonGeometry, buttonMaterial);
  buttonMesh.name = `${tempId}-button`;
  buttonMesh.userData.isbutton = true;
  hotspotGroup.add(buttonMesh);

  const messageText = hotspotText(data.messageText);
  const messageOpacity = data.messageOpacity ?? 1;
  const popupType = data.messagePopupType || 'normal';

  const messageLayout = getHotspotPreviewLayout();
  const {
    texture: messageTexture,
    canvasWidth: msgCanvasW,
    canvasHeight: msgCanvasH,
  } = createMessageTexture(messageText, {
    bgColor: data.messageBgColor || '#222222',
    textColor: data.messageTextColor || '#ffffff',
    fontFamily: messageLayout.messageFontFamily,
    fontSize: messageLayout.messageFontSize,
    padding: messageLayout.messagePadding,
    maxWidth: messageLayout.messageMaxWidthPx,
    borderRadius: messageLayout.messageBorderRadius ?? 8,
  });

  const messageGeometry = createMessageGeometry(msgCanvasW, msgCanvasH, 100);
  const msgWorldW = msgCanvasW / 100;
  const msgWorldH = msgCanvasH / 100;

  const messageMaterial = new THREE.MeshBasicMaterial({
    map: messageTexture,
    color: '#ffffff',
    transparent: true,
    opacity: 0,
    side: THREE.DoubleSide,
    alphaTest: 0.01,
    depthTest: false,
    depthWrite: false,
    fog: false,
  });
  const messageMesh = new THREE.Mesh(messageGeometry, messageMaterial);
  messageMesh.name = `${tempId}-message`;
  messageMesh.userData.nonSelectable = true;
  messageMesh.userData.messageWorldWidth = msgWorldW;
  messageMesh.userData.messageWorldHeight = msgWorldH;
  messageMesh.userData.messageLayoutMaxWidthPx =
    messageLayout.messageMaxWidthPx;
  messageMesh.raycast = () => null;
  const initialMsgScale =
    messageLayout.messageBaseScale || HOTSPOT_MESSAGE_BASE_SCALE;
  messageMesh.scale.setScalar(initialMsgScale);
  positionHotspotMessageNearButton(
    messageMesh,
    buttonMesh,
    msgWorldW,
    msgWorldH,
    initialMsgScale
  );
  const showMessageForEdit = messageWasVisible || previewMessageOpacity;
  messageMesh.visible = showMessageForEdit;
  messageMesh.material.opacity = showMessageForEdit ? messageOpacity : 0;
  messageMesh.material.needsUpdate = true;
  popupAnimations.delete(messageMesh.uuid);
  hotspotGroup.add(messageMesh);

  // Replace userData with a new object to avoid mutating a frozen/read-only object
  hotspotGroup.userData = {
    ...hotspotGroup.userData,
    hotspot: {
      ...data,
      id: tempId,
      buttonText: hotspotText(data.buttonText),
      messageText: hotspotText(data.messageText),
    },
    interactionType: data.functionType || 'Click',
    popupType,
    messageOpacity,
    _messageMesh: messageMesh,
    isHotspot: true,
    isbutton: true,
    baseWorldSize:
      hotspotGroup.userData.baseWorldSize ?? HOTSPOT_REFERENCE_WORLD_SIZE,
    baseScale: hotspotGroup.userData.baseScale || hotspotGroup.scale.clone(),
  };

  if (camera && renderer) {
    hotspotRegistry.register(
      tempId,
      hotspotGroup,
      messageMesh,
      hotspotGroup.userData.interactionType,
      camera,
      renderer,
      popupType,
      messageOpacity
    );
  } else {
    hotspotGroup.userData._messageMesh = messageMesh;
  }

  // Sync Redux assets_data so AppLeftSection (hierarchy/asset list) shows updated name and fields
  const currentState = store.getState();
  const hotspotAssets = currentState.assets_object?.assets_data?.hotspot || [];
  const updatedList = hotspotAssets.map((asset) => {
    const assetId = asset._id ?? asset.id ?? asset.url ?? asset.name;
    if (assetId === tempId) {
      return {
        ...asset,
        // Preserve existing name/file_name instead of overwriting
        buttonShape: data.buttonShape || 'Circle',
        buttonText: hotspotText(data.buttonText),
        buttonColor: data.buttonColor || '#4A90D9',
        buttonTextColor: data.buttonTextColor || '#ffffff',
        buttonOpacity: data.buttonOpacity ?? 1,
        functionType: data.functionType || 'Click',
        messageShape: data.messageShape || 'Rectangle',
        messageText: hotspotText(data.messageText),
        messageBgColor: data.messageBgColor || '#222222',
        messageTextColor: data.messageTextColor || '#ffffff',
        messageOpacity: data.messageOpacity ?? 1,
        messagePopupType: data.messagePopupType || 'normal',
        focusDistance: data.focusDistance ?? 10,
        userData: { ...hotspotGroup.userData },
      };
    }
    return asset;
  });
  store.dispatch(update_assets_data({ type: 'hotspot', data: updatedList }));

  return hotspotGroup;
};

const degToRad = (deg) => (deg * Math.PI) / 180;

/**
 * Restores a hotspot from saved backend data (e.g. on project load).
 * Uses existingId so the scene object keeps the backend _id.
 */
export const restoreHotspot = (object, assetData) => {
  const data = {
    buttonShape: assetData.buttonShape || 'Circle',
    buttonText: hotspotText(assetData.buttonText),
    buttonColor: assetData.buttonColor || '#484848',
    buttonTextColor: assetData.buttonTextColor || '#000000',
    buttonOpacity: assetData.buttonOpacity ?? 0.5,
    autoFocus: !!assetData.autoFocus,
    functionType: assetData.functionType || 'Click',
    messageShape: assetData.messageShape || 'Plane',
    messageText: hotspotText(assetData.messageText),
    messageBgColor: assetData.messageBgColor || '#000000',
    messageTextColor: assetData.messageTextColor || '#ffffff',
    messageOpacity: assetData.messageOpacity ?? 0.5,
    messagePopupType: assetData.messagePopupType || 'normal',
    focusDistance: assetData.focusDistance ?? 10,
  };
  const id =
    assetData._id?.toString() || assetData.id || `hotspot-${Date.now()}`;
  const group = handleAddHotSpot(
    object,
    data,
    id,
    null,
    null,
    false,
    assetData.file_name || assetData.name // Use file_name as the override name
  );
  if (!group) return null;
  group.position.set(
    Number(assetData.position_x || 0),
    Number(assetData.position_y || 0),
    Number(assetData.position_z || 0)
  );
  group.rotation.set(
    degToRad(Number(assetData.rotation_x || 0)),
    degToRad(Number(assetData.rotation_y || 0)),
    degToRad(Number(assetData.rotation_z || 0))
  );
  group.scale.set(
    Number(assetData.scale_x ?? 1) || (assetData.scale_x === 0 ? 0 : 1),
    Number(assetData.scale_y ?? 1) || (assetData.scale_y === 0 ? 0 : 1),
    Number(assetData.scale_z ?? 1) || (assetData.scale_z === 0 ? 0 : 1)
  );
  // Store the restored scale/rotation as base values for later restoration
  setHotspotUserDataClone(group, 'baseScale', group.scale, (s) => s.clone());
  syncHotspotDesignQuaternion(group);
  // Restore the file_name from saved data (used for display in UI)
  // NOTE: Do NOT change group.name - it's used for scene lookups by ID
  if (assetData.file_name) {
    group.file_name = assetData.file_name;
    // Also update userData if the hotspot object is stored there
    if (group.userData?.hotspot) {
      group.userData.hotspot.file_name = assetData.file_name;
    }
  }
  applyStoredPivotToObject(group, assetData, {
    projectId:
      typeof window !== 'undefined'
        ? window.location?.pathname?.split('/').filter(Boolean).pop()
        : null,
    cacheKeys: [
      assetData._id,
      assetData.id,
      assetData.name,
      assetData.temp_file_name,
      assetData.file_name,
      id,
    ],
  });
  return group;
};

// ─── Public helpers for external raycaster systems ───────────────────────────

/**
 * Call this from your own raycaster when a hotspot is hovered.
 * Respects the popupType stored in userData.
 */
export const handleHotspotHover = (hotspotGroup, isHovered) => {
  if (!hotspotGroup?.userData?.isHotspot) return;
  if (hotspotGroup.userData.interactionType !== 'Hover') return;

  const msg =
    hotspotGroup.userData._messageMesh ??
    hotspotGroup.getObjectByName(`${hotspotGroup.name}-message`);

  if (msg) {
    setMessageVisible(
      msg,
      isHovered,
      hotspotGroup.userData.popupType || 'normal',
      hotspotGroup.userData.messageOpacity ?? 1
    );
  }
};

/**
 * Call this from your own raycaster when a hotspot is clicked.
 * Respects the popupType stored in userData.
 * In preview mode, also focuses the camera on the hotspot at distance 10.
 *
 * @param {THREE.Group} hotspotGroup - The hotspot group that was clicked
 * @param {THREE.Camera} [camera] - Optional camera to focus on the hotspot
 * @param {number} [focusDistance=10] - Distance to focus the camera from the hotspot
 */
export const handleHotspotClick = (
  hotspotGroup,
  camera = null,
  focusDistance = 10
) => {
  if (!hotspotGroup?.userData?.isHotspot) return;

  // Focus camera if provided
  if (camera && camera.isCamera) {
    focusCameraOnHotspot(hotspotGroup, camera, focusDistance);
  }

  // Toggle message visibility only if interaction type is 'Click'
  if (hotspotGroup.userData.interactionType !== 'Click') return;

  const msg =
    hotspotGroup.userData._messageMesh ??
    hotspotGroup.getObjectByName(`${hotspotGroup.name}-message`);

  if (msg) {
    const isCurrentlyVisible = msg.visible && msg.material.opacity > 0;
    setMessageVisible(
      msg,
      !isCurrentlyVisible,
      hotspotGroup.userData.popupType || 'normal',
      hotspotGroup.userData.messageOpacity ?? 1
    );
  }
};

/**
 * Focuses the camera on a hotspot at the specified distance.
 * Smoothly animates the camera to the new position.
 *
 * @param {THREE.Group} hotspotGroup - The hotspot to focus on
 * @param {THREE.Camera} camera - The camera to move
 * @param {number} [distance=10] - Distance from the hotspot to position the camera
 * @param {number} [duration=2000] - Animation duration in milliseconds
 */
export const HOTSPOT_CAMERA_FOCUS_DURATION_MS = 800;

export const focusCameraOnHotspot = (
  hotspotGroup,
  camera,
  distance = 10,
  duration = HOTSPOT_CAMERA_FOCUS_DURATION_MS
) => {
  if (!camera?.isCamera || !hotspotGroup) return;

  // Get hotspot world position
  const hotspotWorldPos = new THREE.Vector3();
  hotspotGroup.getWorldPosition(hotspotWorldPos);

  // Calculate camera direction using hotspot's design-time quaternion
  let designQuaternion = hotspotGroup.quaternion.clone();
  if (sceneStateHelper && sceneStateHelper.initialStates) {
    const savedState = sceneStateHelper.initialStates.get(hotspotGroup.uuid);
    if (savedState && savedState.quaternion) {
      designQuaternion.copy(savedState.quaternion);
    }
  }
  const cameraDirection = new THREE.Vector3(0, 0, 1).applyQuaternion(designQuaternion).normalize();

  // Target camera position: distance from hotspot in the design-time direction
  const targetPos = hotspotWorldPos
    .clone()
    .add(cameraDirection.clone().multiplyScalar(distance));

  // Smooth animation using requestAnimationFrame
  const startPos = camera.position.clone();
  const startTime = Date.now();

  const animateCamera = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function (ease-in-out)
    const eased =
      progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress;

    // Interpolate camera position
    camera.position.lerpVectors(startPos, targetPos, eased);
    camera.lookAt(hotspotWorldPos);

    if (progress < 1) {
      requestAnimationFrame(animateCamera);
    }
  };

  requestAnimationFrame(animateCamera);
};

/**
 * Show the hotspot message text. Does not toggle; always shows.
 */
export const showHotspotMessage = (hotspotGroup) => {
  if (!hotspotGroup?.userData?.isHotspot) return;
  const msg =
    hotspotGroup.userData._messageMesh ??
    hotspotGroup.getObjectByName(`${hotspotGroup.name}-message`);
  if (msg) {
    setMessageVisible(
      msg,
      true,
      hotspotGroup.userData.popupType || 'normal',
      hotspotGroup.userData.messageOpacity ?? 1
    );
  }
};

/**
 * Toggle the hotspot message: if visible, hide it; if hidden, show it (e.g. alternate on each click in preview).
 */
export const toggleHotspotMessage = (hotspotGroup) => {
  if (!hotspotGroup?.userData?.isHotspot) return;
  const msg =
    hotspotGroup.userData._messageMesh ??
    hotspotGroup.getObjectByName(`${hotspotGroup.name}-message`);
  if (msg) {
    const isVisible = msg.visible && msg.material.opacity > 0;
    setMessageVisible(
      msg,
      !isVisible,
      hotspotGroup.userData.popupType || 'normal',
      hotspotGroup.userData.messageOpacity ?? 1
    );
  }
};

/**
 * Enable or disable billboard for a hotspot.
 * When enabled, hotspot is scaled to a fixed on-screen button size (scaled by canvas).
 * When faceCamera is true (preview), hotspot faces the camera; when false (design), user rotation is kept.
 *
 * @param {THREE.Group} hotspotGroup - The hotspot group (from sceneHotspots)
 * @param {boolean}     enabled     - true = scale + optional face camera, false = no-op
 * @param {THREE.WebGLRenderer} [renderer] - If provided when enabled, scale hotspot to fixed px on screen
 * @param {boolean} [faceCamera=true] - If true, copy camera quaternion each frame (preview); if false, restore design rotation (design)
 */
export const setHotspotBillboardEnabled = (
  hotspotGroup,
  enabled,
  renderer = null,
  faceCamera = true
) => {
  if (!hotspotGroup?.isGroup) return;
  const buttonMesh = hotspotGroup.getObjectByName(
    `${hotspotGroup.name}-button`
  );
  if (!buttonMesh?.isMesh) return;

  // Store original scale if not already stored
  if (!hotspotGroup.userData.baseScale) {
    setHotspotUserDataClone(
      hotspotGroup,
      'baseScale',
      hotspotGroup.scale,
      (s) => s.clone()
    );
  }
  ensureHotspotBaseQuaternion(hotspotGroup);

  if (enabled) {
    if (faceCamera) {
      ensureHotspotBaseQuaternion(hotspotGroup);
    }

    buttonMesh.onBeforeRender = (r, _scene, cam) => {
      if (faceCamera) {
        cam.getWorldQuaternion(hotspotGroup.quaternion);
      }

      let s;
      if (faceCamera) {
        const layout = getHotspotPreviewLayout(renderer || r);
        // Preview: fixed on-screen button size (slightly smaller on narrow canvases)
        let viewportHeight = HOTSPOT_VIEWPORT_REF_HEIGHT;
        if (renderer?.domElement) {
          viewportHeight = renderer.domElement.clientHeight || viewportHeight;
        } else if (r?.domElement) {
          viewportHeight = r.domElement.clientHeight || viewportHeight;
        } else if (typeof window !== 'undefined' && window.innerHeight) {
          viewportHeight = window.innerHeight;
        }
        const effectiveH = Math.min(viewportHeight, HOTSPOT_VIEWPORT_REF_HEIGHT);
        const targetButtonPx = layout.buttonTargetPx;

        let worldSizeForPx;
        if (cam.isOrthographicCamera) {
          const visibleWorldHeight = (cam.top - cam.bottom) / (cam.zoom || 1);
          worldSizeForPx = (targetButtonPx / effectiveH) * visibleWorldHeight;
        } else {
          hotspotGroup.getWorldPosition(_hotspotWorldPos);
          const dist = cam.position.distanceTo(_hotspotWorldPos);
          const fovRad = (cam.fov * Math.PI) / 180;
          worldSizeForPx =
            (targetButtonPx / effectiveH) *
            2 *
            dist *
            Math.tan(fovRad / 2);
        }

        s = worldSizeForPx / HOTSPOT_REFERENCE_WORLD_SIZE;

        if (hotspotGroup.parent && hotspotGroup.parent.type !== 'Scene') {
          hotspotGroup.parent.getWorldScale(_hotspotParentScale);
          const parentUniform =
            Math.max(
              _hotspotParentScale.x,
              _hotspotParentScale.y,
              _hotspotParentScale.z
            ) || 1;
          s /= parentUniform;
        }
        hotspotGroup.scale.setScalar(Math.max(0.05, s));

        // Fit after group scale so projection matches the final on-screen size.
        const messageMesh =
          hotspotGroup.userData._messageMesh ||
          hotspotGroup.getObjectByName(`${hotspotGroup.name}-message`);
        if (messageMesh?.isMesh) {
          const geoW = messageMesh.geometry?.parameters?.width;
          const geoH = messageMesh.geometry?.parameters?.height;
          const msgWorldW =
            (Number.isFinite(geoW) && geoW > 0
              ? geoW
              : messageMesh.userData.messageWorldWidth) || 0;
          const msgWorldH =
            (Number.isFinite(geoH) && geoH > 0
              ? geoH
              : messageMesh.userData.messageWorldHeight) || 0;
          let preferredScale =
            layout.messageBaseScale || HOTSPOT_MESSAGE_BASE_SCALE;
          if (msgWorldW > 0 && layout.messageMaxScreenPx > 0) {
            const naturalScreenW =
              (msgWorldW / HOTSPOT_REFERENCE_WORLD_SIZE) * targetButtonPx;
            if (naturalScreenW > layout.messageMaxScreenPx) {
              preferredScale *= layout.messageMaxScreenPx / naturalScreenW;
            }
          }
          fitHotspotMessageInsideCanvas({
            messageMesh,
            buttonMesh,
            hotspotGroup,
            camera: cam,
            renderer: renderer || r,
            msgWorldW,
            msgWorldH,
            preferredScale,
          });
        }
        hotspotGroup.updateMatrixWorld(true);
        return;
      } else {
        // Design: keep user rotation; only restore base scale for billboard sizing
        hotspotGroup.scale.copy(hotspotGroup.userData.baseScale);
        const messageMesh =
          hotspotGroup.userData._messageMesh ||
          hotspotGroup.getObjectByName(`${hotspotGroup.name}-message`);
        if (messageMesh?.isMesh) {
          messageMesh.scale.setScalar(1);
          positionHotspotMessageNearButton(
            messageMesh,
            buttonMesh,
            messageMesh.userData.messageWorldWidth ||
              messageMesh.geometry?.parameters?.width ||
              0,
            messageMesh.userData.messageWorldHeight ||
              messageMesh.geometry?.parameters?.height ||
              0,
            1
          );
        }
        hotspotGroup.updateMatrixWorld(true);
        return;
      }
    };
  } else {
    // Restore original scale/rotation when disabling billboard
    restoreHotspotDesignRotation(hotspotGroup);
    hotspotGroup.scale.copy(hotspotGroup.userData.baseScale);
    const messageMesh =
      hotspotGroup.userData._messageMesh ||
      hotspotGroup.getObjectByName(`${hotspotGroup.name}-message`);
    if (messageMesh?.isMesh) {
      messageMesh.scale.setScalar(1);
      positionHotspotMessageNearButton(
        messageMesh,
        buttonMesh,
        messageMesh.userData.messageWorldWidth ||
          messageMesh.geometry?.parameters?.width ||
          0,
        messageMesh.userData.messageWorldHeight ||
          messageMesh.geometry?.parameters?.height ||
          0,
        1
      );
    }
    buttonMesh.onBeforeRender = () => {};
  }
};

/**
 * Rebuild hotspot message cards for the current canvas size.
 * Call when preview frame size changes so wrap width / fonts match the viewport.
 */
export function refreshHotspotMessageLayouts(hotspotList = [], renderer = null) {
  const layout = getHotspotPreviewLayout(renderer);
  (hotspotList || []).forEach((hotspotGroup) => {
    if (!hotspotGroup?.userData?.isHotspot) return;
    applyHotspotMessageLayout(hotspotGroup, layout);
  });
}

function applyHotspotMessageLayout(hotspotGroup, layout) {
  const tempId = hotspotGroup.name;
  const messageMesh =
    hotspotGroup.userData._messageMesh ||
    hotspotGroup.getObjectByName(`${tempId}-message`);
  if (!messageMesh?.isMesh) return;

  if (messageMesh.userData.messageLayoutMaxWidthPx === layout.messageMaxWidthPx) {
    const buttonMesh = hotspotGroup.getObjectByName(`${tempId}-button`);
    positionHotspotMessageNearButton(
      messageMesh,
      buttonMesh,
      messageMesh.userData.messageWorldWidth ||
        messageMesh.geometry?.parameters?.width ||
        0,
      messageMesh.userData.messageWorldHeight ||
        messageMesh.geometry?.parameters?.height ||
        0,
      messageMesh.scale.x || 1
    );
    return;
  }

  const data = hotspotGroup.userData.hotspot || {};
  const messageText = hotspotText(data.messageText);
  const wasVisible = messageMesh.visible;
  const wasOpacity = messageMesh.material?.opacity ?? 0;

  const {
    texture: messageTexture,
    canvasWidth: msgCanvasW,
    canvasHeight: msgCanvasH,
  } = createMessageTexture(messageText, {
    bgColor: data.messageBgColor || '#222222',
    textColor: data.messageTextColor || '#ffffff',
    fontFamily: layout.messageFontFamily || HOTSPOT_MESSAGE_FONT_FAMILY,
    fontSize: layout.messageFontSize || 18,
    padding: layout.messagePadding || 12,
    maxWidth: layout.messageMaxWidthPx,
    borderRadius: layout.messageBorderRadius ?? 8,
  });

  const msgWorldW = msgCanvasW / 100;
  const msgWorldH = msgCanvasH / 100;

  const buttonMesh = hotspotGroup.getObjectByName(`${tempId}-button`);

  const oldMap = messageMesh.material?.map;
  const oldGeom = messageMesh.geometry;
  messageMesh.geometry = createMessageGeometry(msgCanvasW, msgCanvasH, 100);
  if (messageMesh.material) {
    messageMesh.material.map = messageTexture;
    messageMesh.material.needsUpdate = true;
  }
  messageMesh.userData.messageWorldWidth = msgWorldW;
  messageMesh.userData.messageWorldHeight = msgWorldH;
  messageMesh.userData.messageLayoutMaxWidthPx = layout.messageMaxWidthPx;
  messageMesh.scale.setScalar(layout.messageBaseScale || HOTSPOT_MESSAGE_BASE_SCALE);
  positionHotspotMessageNearButton(
    messageMesh,
    buttonMesh,
    msgWorldW,
    msgWorldH,
    layout.messageBaseScale || HOTSPOT_MESSAGE_BASE_SCALE
  );
  messageMesh.visible = wasVisible;
  if (messageMesh.material) {
    messageMesh.material.opacity = wasOpacity;
  }

  oldGeom?.dispose?.();
  oldMap?.dispose?.();
}

/**
 * Fully removes a hotspot from the scene and cleans up GPU resources.
 */
export const removeHotspot = (scene, hotspotGroup) => {
  if (!hotspotGroup) return;
  hotspotRegistry.unregister(hotspotGroup.name);
  // Cancel any running animations for meshes in this group
  hotspotGroup.traverse((obj) => {
    if (obj.isMesh) popupAnimations.delete(obj.uuid);
  });
  scene.remove(hotspotGroup);
  hotspotGroup.traverse((obj) => {
    obj.geometry?.dispose();
    if (obj.material) {
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => {
        m.map?.dispose();
        m.dispose();
      });
    }
  });
};

/**
 * Adds a default hotspot to the grid with preset values.
 * Called when user clicks "Hotspots" in the Library menu.
 *
 * @param {Object} object - Object containing { scene, camera, renderer }
 * @returns {THREE.Group} The created hotspot group
 */
export const handleAddDefaultHotspot = (object) => {
  if (!object?.scene) {
    console.warn(
      'handleAddDefaultHotspot: object with scene property is required.'
    );
    return null;
  }

  // Use default hotspot data from constants
  const defaultData = { ...HotSpotInitialData };

  // Add the hotspot with default values
  const hotspotGroup = handleAddHotSpot(
    object,
    defaultData,
    null, // existingId
    null, // parentObject
    null, // explicitParentId
    true // showToast
  );

  return hotspotGroup;
};

/**
 * Duplicates a hotspot asset with an offset position and copy naming.
 * Used for both menu duplication and Ctrl+C / Ctrl+V copy-paste.
 *
 * @param {Object} hotspotItem - The hotspot asset data from Redux
 * @param {Object} objectt - Object containing { scene, camera, renderer, ... }
 */
export const duplicateHotspot = (hotspotItem, objectt) => {
  if (!hotspotItem || !objectt?.scene) {
    console.warn(
      'duplicateHotspot: hotspot item and objectt.scene are required'
    );
    return null;
  }

  const { scene } = objectt;

  // Get the actual Three.js hotspot group from the scene
  const hotspotObj = scene.getObjectByName(
    hotspotItem._id || hotspotItem.id || hotspotItem.name
  );

  if (!hotspotObj) {
    console.error('Hotspot object not found in scene');
    return null;
  }

  const hotspotId =
    hotspotItem._id || hotspotItem.id || hotspotItem.url || hotspotItem.name;
  const freshHotspotItem =
    store.getState().assets_object?.assets_data?.hotspot?.find(
      (asset) =>
        asset._id === hotspotId ||
        asset.id === hotspotId ||
        asset.url === hotspotId ||
        asset.name === hotspotId
    ) || hotspotItem;

  // Renamed hotspots update file_name only; name keeps the original label/id.
  const baseName = getHotspotDisplayName(freshHotspotItem, hotspotObj);

  const hotspotAssets =
    store.getState().assets_object?.assets_data?.hotspot || [];
  const copyName = `${baseName}_copy ${countHotspotCopies(hotspotAssets, baseName) + 1}`;

  const newHotspotData = {
    buttonShape: hotspotItem.buttonShape || 'Circle',
    buttonText: hotspotText(hotspotItem.buttonText),
    buttonColor: hotspotItem.buttonColor || '#4A90D9',
    buttonTextColor: hotspotItem.buttonTextColor || '#ffffff',
    buttonOpacity: hotspotItem.buttonOpacity ?? 1,
    autoFocus: hotspotItem.autoFocus || false,
    functionType: hotspotItem.functionType || 'Click',
    messageShape: hotspotItem.messageShape || 'Rectangle',
    messageText: hotspotText(hotspotItem.messageText),
    messageBgColor: hotspotItem.messageBgColor || '#222222',
    messageTextColor: hotspotItem.messageTextColor || '#ffffff',
    messageOpacity: hotspotItem.messageOpacity ?? 1,
    messagePopupType: hotspotItem.messagePopupType || 'normal',
    focusDistance: hotspotItem.focusDistance ?? 10,
  };

  // Add with slight offset from original position; pass copy name so add_new_assets uses it
  const newHotspotGroup = handleAddHotSpot(
    objectt,
    newHotspotData,
    null,
    null,
    null,
    false,
    copyName
  );

  if (newHotspotGroup) {
    // Offset the new hotspot slightly from the original
    newHotspotGroup.position.copy(hotspotObj.position);
    newHotspotGroup.position.addScalar(0.5);
    newHotspotGroup.rotation.copy(hotspotObj.rotation);
    newHotspotGroup.scale.copy(hotspotObj.scale);

    newHotspotGroup.file_name = copyName;
    if (newHotspotGroup.userData) {
      newHotspotGroup.userData.file_name = copyName;
    }

    syncDuplicatedHotspotInRedux(newHotspotGroup, copyName);
    selectObjectInScene(newHotspotGroup._id);
  }

  return newHotspotGroup;
};

export const setHotspotRegistryPreviewMode = (enabled) => {
  hotspotRegistry.previewMode = !!enabled;
};

/**
 * Register any hotspots that were created before camera/renderer existed
 * (e.g. shared preview-page load) and refresh registry camera references.
 */
export function ensureHotspotRegistryForScene(
  hotspotList = [],
  camera,
  renderer
) {
  if (!camera || !renderer?.domElement) return;

  hotspotRegistry.camera = camera;
  hotspotRegistry.renderer = renderer;

  (hotspotList || []).forEach((hotspotGroup) => {
    if (!hotspotGroup?.userData?.isHotspot) return;
    const tempId = hotspotGroup.name;
    const messageMesh =
      hotspotGroup.userData._messageMesh ||
      hotspotGroup.getObjectByName(`${tempId}-message`);
    if (!messageMesh) return;

    hotspotGroup.userData._messageMesh = messageMesh;
    hotspotRegistry.register(
      tempId,
      hotspotGroup,
      messageMesh,
      hotspotGroup.userData.interactionType || 'Click',
      camera,
      renderer,
      hotspotGroup.userData.popupType || 'normal',
      hotspotGroup.userData.messageOpacity ?? 1
    );
  });
}

/** Shared preview-page + in-editor preview: registry, layout, billboard. */
export function syncHotspotsForPreviewMode(
  hotspotList = [],
  camera,
  renderer
) {
  ensureHotspotRegistryForScene(hotspotList, camera, renderer);
  try {
    refreshHotspotMessageLayouts(hotspotList, renderer);
  } catch (_) {
    /* ignore */
  }
  (hotspotList || []).forEach((hp) => {
    try {
      setHotspotBillboardEnabled(hp, true, renderer, true);
    } catch (_) {
      /* ignore */
    }
  });
}
